/**
 * InternalToastContainer — self-contained toast system for master-pattern-view.
 *
 * Uses a DOM CustomEvent bus so `useToast` can fire toasts without requiring
 * the consumer to provide a `toast` prop or mount a ToastManager.
 *
 * Usage:
 *   - `fireInternalToast(type, message)` — fire from anywhere in the package
 *   - `<InternalToastContainer />` — mount once inside MasterPatternView
 *
 * @module components/InternalToastContainer
 */
import React, { useEffect } from 'react';
import { toast } from '../../Toast/ToastProvider';
import type { ToastType } from '../hooks/useToast';

const TOAST_EVENT = 'mpv:toast';

interface ToastEntry {
  id: string;
  type: ToastType;
  message: string;
}

export function fireInternalToast(type: ToastType, message: string): void {
  const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : String(Date.now() + Math.random());
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { id, type, message } }));
}

export function InternalToastContainer(): React.ReactElement | null {
  useEffect(() => {
    const handler = (e: Event) => {
      const entry = (e as CustomEvent<ToastEntry>).detail;
      const t = toast[entry.type === 'error' ? 'error' : entry.type === 'success' ? 'success' : entry.type === 'warning' ? 'warning' : 'info'];
      if(t){
          t(entry.message);
      } else {
          toast(entry.message);
      }
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, []);

  return null;
}
